import React, { useState, useEffect } from 'react';
import { tenantApi, formatApiErrorDetail, userApi } from '../../lib/api';
import { formatDate, getStatusColor, getContractTypeColor } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Building2,
  Plus,
  Search,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  Eye,
  Loader2,
  Filter,
  KeyRound,
} from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../../components/Pagination';

export default function TenantsPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [contractFilter, setContractFilter] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [creating, setCreating] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 0 });
  const [formData, setFormData] = useState({
    company_name: '',
    owner_name: '',
    email: '',
    phone: '',
    address: '',
    contract_type: 'NON_EXCLUSIVE',
  });

  const fetchTenants = async () => {
    try {
      const params = { page, limit: 20 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (contractFilter !== 'all') params.contract_type = contractFilter;
      
      const { data } = await tenantApi.list(params);
      setTenants(data.items || data);
      setPagination({ total: data.total || 0, pages: data.pages || 0 });
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, [statusFilter, contractFilter, page]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await tenantApi.create(formData);
      toast.success('Tenant created successfully');
      setShowCreateDialog(false);
      setFormData({
        company_name: '',
        owner_name: '',
        email: '',
        phone: '',
        address: '',
        contract_type: 'NON_EXCLUSIVE',
      });
      fetchTenants();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to create tenant');
    } finally {
      setCreating(false);
    }
  };

  const handleApprove = async (tenantId) => {
    try {
      const { data } = await tenantApi.approve(tenantId);
      toast.success(`Tenant approved. Temp password: ${data.temp_password}`);
      fetchTenants();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to approve tenant');
    }
  };

  const handleResendCredentials = async (tenant) => {
    try {
      const { data } = await tenantApi.resendCredentials(tenant.id);
      const pw = data.temp_password;
      try {
        await navigator.clipboard.writeText(pw);
        toast.success(`Password reset for ${data.email}. New password copied to clipboard!`);
      } catch {
        toast.success(`Password reset for ${data.email}. New password: ${pw}`);
      }
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to reset password');
    }
  };

  const handleSuspend = async (tenantId) => {
    try {
      await tenantApi.suspend(tenantId);
      toast.success('Tenant suspended');
      fetchTenants();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to suspend tenant');
    }
  };

  const handleActivate = async (tenantId) => {
    try {
      await tenantApi.activate(tenantId);
      toast.success('Tenant activated');
      fetchTenants();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to activate tenant');
    }
  };

  const handleUpdateContract = async (tenantId, contractType) => {
    try {
      await tenantApi.update(tenantId, { contract_type: contractType });
      toast.success('Contract type updated');
      fetchTenants();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to update contract');
    }
  };

  const filteredTenants = tenants.filter((tenant) =>
    tenant.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tenant.owner_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    tenant.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Tenants
            </h1>
            <p className="text-slate-600 mt-1">Manage shop owner accounts</p>
          </div>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-[#005DE8] hover:bg-[#0047B3]"
            data-testid="create-tenant-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Tenant
          </Button>
        </div>

        {/* Filters */}
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search tenants..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-11"
                    data-testid="search-tenants"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40 h-11" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="PENDING">Pending</SelectItem>
                  <SelectItem value="SUSPENDED">Suspended</SelectItem>
                </SelectContent>
              </Select>
              <Select value={contractFilter} onValueChange={setContractFilter}>
                <SelectTrigger className="w-full sm:w-40 h-11" data-testid="contract-filter">
                  <SelectValue placeholder="Contract" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Contracts</SelectItem>
                  <SelectItem value="EXCLUSIVE">Exclusive</SelectItem>
                  <SelectItem value="NON_EXCLUSIVE">Non-Exclusive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tenants Table */}
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" />
              </div>
            ) : filteredTenants.length > 0 ? (
              <>
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100">
                    <TableHead>Company</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Contract</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredTenants.map((tenant) => (
                    <TableRow key={tenant.id} className="table-row-hover">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-[#005DE8]" />
                          </div>
                          <span className="font-medium text-slate-900">{tenant.company_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{tenant.owner_name}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <p className="text-slate-900">{tenant.email}</p>
                          <p className="text-slate-500">{tenant.phone}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={tenant.contract_type}
                          onValueChange={(value) => handleUpdateContract(tenant.id, value)}
                          disabled={tenant.status !== 'ACTIVE'}
                        >
                          <SelectTrigger className="w-32 h-8" data-testid={`contract-select-${tenant.id}`}>
                            <Badge className={`${getContractTypeColor(tenant.contract_type)} border`}>
                              {tenant.contract_type === 'EXCLUSIVE' ? 'Exclusive' : 'Non-Exclusive'}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="EXCLUSIVE">Exclusive</SelectItem>
                            <SelectItem value="NON_EXCLUSIVE">Non-Exclusive</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusColor(tenant.status)} border`}>
                          {tenant.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">{formatDate(tenant.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`tenant-actions-${tenant.id}`}>
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedTenant(tenant);
                                setShowViewDialog(true);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              View Details
                            </DropdownMenuItem>
                            {tenant.status === 'PENDING' && (
                              <DropdownMenuItem
                                onClick={() => handleApprove(tenant.id)}
                                className="text-emerald-600"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Approve
                              </DropdownMenuItem>
                            )}
                            {tenant.status === 'ACTIVE' && (
                              <DropdownMenuItem
                                onClick={() => handleSuspend(tenant.id)}
                                className="text-red-600"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Suspend
                              </DropdownMenuItem>
                            )}
                            {tenant.status === 'ACTIVE' && (
                              <DropdownMenuItem
                                onClick={() => handleResendCredentials(tenant)}
                              >
                                <KeyRound className="h-4 w-4 mr-2" />
                                Reset Password
                              </DropdownMenuItem>
                            )}
                            {tenant.status === 'SUSPENDED' && (
                              <DropdownMenuItem
                                onClick={() => handleActivate(tenant.id)}
                                className="text-emerald-600"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Activate
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Pagination page={page} pages={pagination.pages} total={pagination.total} onPageChange={setPage} />
              </>
            ) : (
              <div className="text-center py-16">
                <Building2 className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">No tenants found</h3>
                <p className="text-slate-500 mt-1">Get started by adding your first tenant</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Add New Tenant</DialogTitle>
              <DialogDescription>
                Create a new shop owner account. They will receive an email to set their password.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreate}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    required
                    data-testid="create-company-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="owner_name">Owner Name</Label>
                  <Input
                    id="owner_name"
                    value={formData.owner_name}
                    onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                    required
                    data-testid="create-owner-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="create-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      data-testid="create-phone"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    required
                    rows={2}
                    data-testid="create-address"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contract_type">Contract Type</Label>
                  <Select
                    value={formData.contract_type}
                    onValueChange={(value) => setFormData({ ...formData, contract_type: value })}
                  >
                    <SelectTrigger data-testid="create-contract-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NON_EXCLUSIVE">Non-Exclusive</SelectItem>
                      <SelectItem value="EXCLUSIVE">Exclusive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#005DE8] hover:bg-[#0047B3]"
                  disabled={creating}
                  data-testid="create-tenant-submit"
                >
                  {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Create Tenant
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit' }}>Tenant Details</DialogTitle>
            </DialogHeader>
            {selectedTenant && (
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-blue-50 rounded-xl flex items-center justify-center">
                    <Building2 className="h-8 w-8 text-[#005DE8]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">{selectedTenant.company_name}</h3>
                    <p className="text-slate-500">{selectedTenant.owner_name}</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                  <div className="col-span-2 sm:col-span-1">
                    <p className="text-sm text-slate-500">Email</p>
                    <p className="font-medium break-all">{selectedTenant.email}</p>
                  </div>
                  <div className="col-span-2 sm:col-span-1">
                    <p className="text-sm text-slate-500">Phone</p>
                    <p className="font-medium">{selectedTenant.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Contract Type</p>
                    <Badge className={`${getContractTypeColor(selectedTenant.contract_type)} border mt-1`}>
                      {selectedTenant.contract_type === 'EXCLUSIVE' ? 'Exclusive' : 'Non-Exclusive'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Status</p>
                    <Badge className={`${getStatusColor(selectedTenant.status)} border mt-1`}>
                      {selectedTenant.status}
                    </Badge>
                  </div>
                  <div className="col-span-2">
                    <p className="text-sm text-slate-500">Address</p>
                    <p className="font-medium">{selectedTenant.address}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Created</p>
                    <p className="font-medium">{formatDate(selectedTenant.created_at)}</p>
                  </div>
                  {selectedTenant.approved_at && (
                    <div>
                      <p className="text-sm text-slate-500">Approved</p>
                      <p className="font-medium">{formatDate(selectedTenant.approved_at)}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowViewDialog(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
